/**
 * SNS Links Utils
 *
 * Normalization layer for sns_links:
 * - Decode HTML entities (&amp; etc.) and percent-encoding when safe
 * - Canonicalize common SNS URLs (YouTube/Facebook/Instagram)
 * - Dedupe
 */

import { Patterns } from "../../constants/patterns";
import { extractXStatusUrlFromOfficialEmbedHtml, normalizeXStatusUrl } from "../extractor/x-embed-extractor";

type SnsLinkInput = string | { url?: string | null };
type SnsLinksInput = string | SnsLinkInput[] | null | undefined;

/** Safely decode percent-encoded URL segments. */
function safeDecodeURIComponent(value: string): string {
    if (!value || !value.includes("%")) {
        return value;
    }

    let current = value;
    for (let index = 0; index < 2; index += 1) {
        try {
            const decoded = decodeURIComponent(current);
            if (decoded === current) {
                break;
            }
            current = decoded;
        } catch {
            break;
        }
    }

    return current;
}

/** Remove a single trailing slash from a URL. */
function stripTrailingSlash(url: string): string {
    return url.replace(/\/$/, "");
}

/** Normalize a single SNS URL candidate into a canonical form. */
function normalizeSnsLink(raw: string): string | null {
    if (typeof raw !== "string") {
        return null;
    }

    let value = raw.trim();
    if (!value) {
        return null;
    }

    value = value.replace(/^['"\s]+|['"\s]+$/g, "");
    value = Patterns.decodeAmpersand(value);
    value = safeDecodeURIComponent(value);

    if (
        value.includes("twitter-tweet") ||
        value.includes("platform.twitter.com/widgets.js") ||
        value.includes("twitter.com/") ||
        value.includes("x.com/")
    ) {
        const extracted = extractXStatusUrlFromOfficialEmbedHtml(value);
        const normalizedX = extracted ? normalizeXStatusUrl(extracted) : null;
        if (normalizedX) {
            value = normalizedX;
        }
    }

    if (value.includes("facebook.com/plugins/")) {
        try {
            const url = new URL(value.startsWith("http") ? value : `https://${value}`);
            const href = url.searchParams.get("href");
            if (href) {
                value = safeDecodeURIComponent(Patterns.decodeAmpersand(href)).trim();
            }
        } catch {
            return null;
        }
    }

    if (!/^https?:\/\//i.test(value)) {
        if (
            /^(?:www\.|m\.|fb\.com\/|facebook\.com\/|youtu\.be\/|youtube\.com\/|instagram\.com\/|tiktok\.com\/|vm\.tiktok\.com\/)/i.test(
                value,
            )
        ) {
            value = `https://${value}`;
        } else {
            return null;
        }
    }

    let url: URL;
    try {
        url = new URL(value);
    } catch {
        return null;
    }

    if (url.protocol !== "http:" && url.protocol !== "https:") {
        return null;
    }

    const host = url.hostname.toLowerCase();

    for (const key of Array.from(url.searchParams.keys())) {
        if (key === "fbclid" || key.startsWith("utm_")) {
            url.searchParams.delete(key);
        }
    }

    if (host === "youtu.be" || host.endsWith("youtube.com")) {
        const match = value.match(Patterns.YOUTUBE_ID);
        const videoId = match ? match[1] : null;
        if (videoId) {
            return `https://www.youtube.com/watch?v=${videoId}`;
        }

        url.protocol = "https:";
        url.hostname = "www.youtube.com";
        url.hash = "";
        return stripTrailingSlash(url.toString());
    }

    if (host === "fb.com" || host.endsWith("facebook.com")) {
        url.protocol = "https:";
        url.hostname = "www.facebook.com";
        url.hash = "";
        return stripTrailingSlash(url.toString());
    }

    if (host.endsWith("instagram.com")) {
        url.protocol = "https:";
        url.hostname = "www.instagram.com";
        url.hash = "";
        url.search = "";
        return stripTrailingSlash(url.toString());
    }

    if (host.endsWith("tiktok.com")) {
        url.protocol = "https:";
        url.hostname = "www.tiktok.com";
        url.hash = "";
        url.search = "";
        return stripTrailingSlash(url.toString());
    }

    url.protocol = "https:";
    url.hash = "";
    return stripTrailingSlash(url.toString());
}

export class SNSLinksUtils {
    private static readonly snsUrlAttributePattern = /https?:\/\/[^\s"'<>\]]+/gi;

    /**
     * Normalize and dedupe sns links.
     * Supports arrays of strings or objects with {url}.
     */
    public static normalizeSnsLinks(input: SnsLinksInput): string[] {
        const values: string[] = [];

        if (Array.isArray(input)) {
            input.forEach((item) => {
                if (typeof item === "string") {
                    values.push(item);
                    return;
                }

                if (item && typeof item === "object" && "url" in item) {
                    const urlValue = Reflect.get(item, "url");
                    if (typeof urlValue === "string") {
                        values.push(urlValue);
                    }
                }
            });
        } else if (typeof input === "string") {
            values.push(input);
        }

        const seen = new Set<string>();
        const output: string[] = [];

        values.forEach((value) => {
            const normalized = normalizeSnsLink(value);
            if (!normalized || seen.has(normalized)) {
                return;
            }

            seen.add(normalized);
            output.push(normalized);
        });

        return output;
    }

    public static parseSnsLinksDataAttribute(raw: string | null | undefined): string[] {
        const value = typeof raw === "string" ? raw.trim() : "";

        if (!value) {
            return [];
        }

        try {
            const parsed = JSON.parse(value) as unknown;

            if (typeof parsed === "string") {
                return this.normalizeSnsLinks([parsed]);
            }

            if (Array.isArray(parsed)) {
                return this.normalizeSnsLinks(parsed);
            }
        } catch {
            // Fall through to URL extraction for malformed HTML-decoded attributes.
        }

        const decoded = safeDecodeURIComponent(Patterns.decodeAmpersand(value));
        const extracted = decoded.match(this.snsUrlAttributePattern) || [];

        return this.normalizeSnsLinks(extracted);
    }
}
